import { useMutation } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  type AmountCurrencyRequest,
  type Error as CashierError,
  type CashierPaymentMethod,
  type CashierTradingAccount,
  createSavedPaymentAccount,
  initSessionToken,
  type SavedPaymentAccountSummary,
} from "@/api/cashier";
import { getTokenResultAsync } from "@/api/safeCharge";
import { useDynamicForm } from "@/hooks/deposit/useDynamicForm";
import { useCreditCardForm } from "@/hooks/useCreditCardForm";
import { useLogger } from "@/hooks/useLogger";
import { getMerchantConfigs, isCardForm } from "@/utils/helpers";
import { serializeError } from "@/utils/log";

export type NewProfitMethodParams = {
  tradingAccount: CashierTradingAccount;
  paymentMethod: CashierPaymentMethod;
  amount_with_currency: AmountCurrencyRequest;
  document_id?: string;
};

export type NewProfitMethodError = CashierError | Error;

const isCashierError = (error: unknown): error is CashierError =>
  typeof error === "object" &&
  error !== null &&
  "key" in error &&
  typeof (error as CashierError).key === "string";

export const getNewProfitMethodErrorMessage = (
  error: unknown,
  t: TFunction
): string | undefined => {
  if (!error) {
    return undefined;
  }
  if (isCashierError(error)) {
    // t('common.bad_request')
    // t('common.internal_server_error')
    // t('withdrawal.invalid_form_field_data')
    return t(error.key) || error.message || undefined;
  }
  if (error instanceof Error) {
    return error.message || undefined;
  }
  if (typeof error === "string") {
    return error || undefined;
  }
  return undefined;
};

export const useNewProfitMethod = () => {
  const { tokenizeCreditCard } = useTokenizeCreditCard();
  const { savePaymentAccount } = useSavePaymentAccount();

  return useMutation<
    SavedPaymentAccountSummary | undefined,
    NewProfitMethodError,
    NewProfitMethodParams
  >({
    mutationFn: async (params: NewProfitMethodParams) => {
      if (isCardForm(params.paymentMethod)) {
        return tokenizeCreditCard(params);
      }

      // TODO verify if we need to create new SPA always
      return savePaymentAccount(params);
    },
  });
};

const useSavePaymentAccount = () => {
  const { getFormData } = useDynamicForm();

  const savePaymentAccount = useCallback(
    async ({
      paymentMethod,
      amount_with_currency,
      tradingAccount,
      document_id,
    }: NewProfitMethodParams) => {
      const { document_id: _, ...fields } = getFormData(paymentMethod.id) ?? {};
      const { data } = await createSavedPaymentAccount({
        body: {
          payment_method_id: paymentMethod.id,
          fields,
          amount_with_currency,
          document_id,
        },
        path: {
          tradingAccountID: tradingAccount.id,
          paymentMethodID: paymentMethod.id,
        },
        throwOnError: true,
      });
      return data;
    },
    [getFormData]
  );

  return { savePaymentAccount };
};

const useTokenizeCreditCard = () => {
  const { t } = useTranslation();
  const logger = useLogger();
  const { creditCardFormData, creditCardFormValid } = useCreditCardForm();

  const tokenizeCreditCard = useCallback(
    async ({
      paymentMethod,
      amount_with_currency,
      tradingAccount,
    }: NewProfitMethodParams) => {
      if (!creditCardFormValid) {
        throw new Error(
          t("withdrawals.new_profit_method.credit_card.invalid_form")
        );
      }

      const { nameOnCard } = creditCardFormData;

      // Wrap only the SDK tokenisation calls. Any unexpected failure here is
      // re-thrown as a single user-facing error so we don't leak SDK internals.
      let tokenResult: Awaited<ReturnType<typeof getTokenResultAsync>>;
      try {
        const { data: sessionData } = await initSessionToken({
          path: {
            tradingAccountID: tradingAccount.id,
            paymentMethodID: paymentMethod.id,
          },
          throwOnError: true,
        });
        const sessionToken = String(sessionData.session_token);

        tokenResult = await getTokenResultAsync(window.scard, {
          sessionToken,
          ...getMerchantConfigs(sessionData),
          cardHolderName: nameOnCard,
        });

        if (!tokenResult.ccTempToken) {
          throw new Error("Card tokenisation failed");
        }

        const { data } = await createSavedPaymentAccount({
          body: {
            payment_method_id: paymentMethod.id,
            fields: {},
            amount_with_currency,
            cc_temp_token: tokenResult.ccTempToken,
          },
          path: {
            tradingAccountID: tradingAccount.id,
            paymentMethodID: paymentMethod.id,
          },
          throwOnError: true,
        });

        return data;
      } catch (error) {
        logger.error("Failed to save credit card for withdrawal", {
          error: serializeError(error),
        });
        throw new Error(
          t("withdrawals.new_profit_method.credit_card.failed_to_save_card")
        );
      }
    },
    [creditCardFormData, creditCardFormValid, logger.error, t]
  );

  return { tokenizeCreditCard };
};
